/**
 * SupportTicketDetails – displays and allows editing of ticket priority and classification.
 * Shown in the right sidebar of the Support Inbox when a request is active.
 */
import { useState } from 'react';
import { cn } from '@respectly/ui';
import { Check, ChevronDown, Tag, AlertTriangle } from 'lucide-react';
import type { SupportRequest, SupportPriority, SupportCategory } from '@respectly/shared-types';
import { useSupportStore } from '../../stores/useSupportStore';

interface SupportTicketDetailsProps {
  request: SupportRequest;
  className?: string;
}

const PRIORITY_OPTIONS: { value: SupportPriority; label: string; classes: string }[] = [
  { value: 'low',    label: 'Low',    classes: 'text-zinc-500 dark:text-zinc-400' },
  { value: 'medium', label: 'Medium', classes: 'text-blue-600 dark:text-blue-400' },
  { value: 'high',   label: 'High',   classes: 'text-amber-600 dark:text-amber-400' },
  { value: 'urgent', label: 'Urgent', classes: 'text-red-600 dark:text-red-400' },
];

const CATEGORY_OPTIONS: { value: SupportCategory; label: string }[] = [
  { value: 'billing',         label: 'Billing' },
  { value: 'technical',       label: 'Technical' },
  { value: 'compliance',      label: 'Compliance' },
  { value: 'account',         label: 'Account' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'other',           label: 'Other' },
];

const DEFAULT_PRIORITY: { value: SupportPriority; label: string; classes: string } = { value: 'medium', label: 'Medium', classes: 'text-blue-600 dark:text-blue-400' };
const DEFAULT_CATEGORY: { value: SupportCategory; label: string } = { value: 'other', label: 'Other' };

export function SupportTicketDetails({ request, className }: SupportTicketDetailsProps) {
  const { updateRequest, isPerformingAction } = useSupportStore();

  const [priorityOpen, setPriorityOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [savingField, setSavingField] = useState<'priority' | 'category' | null>(null);

  const handlePriorityChange = async (priority: SupportPriority) => {
    setPriorityOpen(false);
    if (priority === request.priority) return;
    setSavingField('priority');
    await updateRequest(request.id, { priority });
    setSavingField(null);
  };

  const handleCategoryChange = async (category: SupportCategory) => {
    setCategoryOpen(false);
    if (category === request.category) return;
    setSavingField('category');
    await updateRequest(request.id, { category });
    setSavingField(null);
  };

  const currentPriority = PRIORITY_OPTIONS.find((p) => p.value === request.priority) ?? DEFAULT_PRIORITY;
  const currentCategory = CATEGORY_OPTIONS.find((c) => c.value === request.category) ?? DEFAULT_CATEGORY;

  return (
    <div className={cn('border-b border-zinc-200 dark:border-zinc-800', className)}>
      <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
        <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">Ticket Details</p>
      </div>

      <div className="p-4 space-y-3">
        {/* Priority */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3 text-zinc-400" />
            <span className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
              Priority
            </span>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => { setPriorityOpen((o) => !o); setCategoryOpen(false); }}
              disabled={isPerformingAction}
              className={cn(
                'w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-xs',
                'bg-zinc-50 dark:bg-zinc-800/60 ring-1 ring-zinc-200 dark:ring-zinc-700',
                'hover:bg-zinc-100 dark:hover:bg-zinc-700/60 transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                priorityOpen && 'ring-blue-400 dark:ring-blue-500'
              )}
            >
              <span className={cn('font-medium', currentPriority.classes)}>
                {savingField === 'priority' ? 'Saving…' : currentPriority.label}
              </span>
              <ChevronDown className={cn('h-3 w-3 text-zinc-400 transition-transform', priorityOpen && 'rotate-180')} />
            </button>

            {priorityOpen && (
              <div className="absolute z-20 top-full mt-1 left-0 right-0 rounded-lg bg-white dark:bg-zinc-900 ring-1 ring-zinc-200 dark:ring-zinc-700 shadow-lg overflow-hidden">
                {PRIORITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handlePriorityChange(opt.value)}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-2 text-xs transition-colors',
                      'hover:bg-zinc-50 dark:hover:bg-zinc-800',
                      opt.value === request.priority && 'bg-zinc-50 dark:bg-zinc-800/60'
                    )}
                  >
                    <span className={cn('font-medium', opt.classes)}>{opt.label}</span>
                    {opt.value === request.priority && (
                      <Check className="h-3 w-3 text-blue-500" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category / Classification */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Tag className="h-3 w-3 text-zinc-400" />
            <span className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
              Classification
            </span>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => { setCategoryOpen((o) => !o); setPriorityOpen(false); }}
              disabled={isPerformingAction}
              className={cn(
                'w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-xs',
                'bg-zinc-50 dark:bg-zinc-800/60 ring-1 ring-zinc-200 dark:ring-zinc-700',
                'hover:bg-zinc-100 dark:hover:bg-zinc-700/60 transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                categoryOpen && 'ring-blue-400 dark:ring-blue-500'
              )}
            >
              <span className="font-medium text-zinc-700 dark:text-zinc-300">
                {savingField === 'category' ? 'Saving…' : currentCategory.label}
              </span>
              <ChevronDown className={cn('h-3 w-3 text-zinc-400 transition-transform', categoryOpen && 'rotate-180')} />
            </button>

            {categoryOpen && (
              <div className="absolute z-20 top-full mt-1 left-0 right-0 rounded-lg bg-white dark:bg-zinc-900 ring-1 ring-zinc-200 dark:ring-zinc-700 shadow-lg overflow-hidden">
                {CATEGORY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleCategoryChange(opt.value)}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-2 text-xs transition-colors',
                      'hover:bg-zinc-50 dark:hover:bg-zinc-800',
                      opt.value === request.category && 'bg-zinc-50 dark:bg-zinc-800/60'
                    )}
                  >
                    <span className="font-medium text-zinc-700 dark:text-zinc-300">{opt.label}</span>
                    {opt.value === request.category && (
                      <Check className="h-3 w-3 text-blue-500" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
